import { storeToRefs } from 'pinia'

import createLogger from '@/lib/logger'
import { useAuthStore } from '@/store/auth'

import { setPostAuthRedirect } from '../postAuthRedirect'

import type { RouteLocationNormalized } from 'vue-router'

const logger = createLogger('authGuard')

/**
 * Handles user authentication routes
 * If user is authenticated then they cannot access the login page
 * Otherwise they will be redirected to login
 */
function routeAllowsWithoutEverGptAccess(to: RouteLocationNormalized): boolean {
  return to.matched.some((r) => r.meta.allowWithoutEverGptAccess === true)
}

export const authGuard = async (to: RouteLocationNormalized) => {
  logger.log('Running hook...')
  const { isAuthenticated, accessToken } = storeToRefs(useAuthStore())

  // Anonymous routes (the public GPT share page) never see a login redirect: their
  // API takes callers without a JWT, and inside a frame Keycloak's login page refuses
  // to render at all, so the redirect would be a blank dead end.
  if (to.meta.allowAnonymous) {
    return true
  }

  // Handle unauthenticated users
  // Adding route name check to avoid infinite loops
  if (!isAuthenticated.value) {
    // Share page: allow Keycloak session without EverGPT app auth (token required for API).
    if (routeAllowsWithoutEverGptAccess(to) && Boolean(accessToken.value)) {
      return
    }
    // Save last visited route
    if (to.name !== 'Login') {
      setPostAuthRedirect(window.location.pathname + window.location.search)
      logger.log('User is unauthenticated, redirecting to login page...')
      return { name: 'Login' }
    }
    // Continue to login page
    return true
  }

  // Handle authenticated users
  if (isAuthenticated.value) {
    // Disallow authenticated users to access public pages
    if (to.meta.isPublic) {
      logger.log('User is authenticated, redirecting to new chat...')
      return { name: 'NewChat' }
    }
  }
}
